import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getCurrentSeason } from '../data/seasons';
import { toast } from 'react-toastify';
import { saveSeason } from '../utils/seasonApi';
import { storage } from '../firebaseConfig';
import { validateFile } from '../utils/fileValidation';
import { rateLimiter, formatTimeUntilReset } from '../utils/rateLimiter';
import '../styles/SaveStrategyModal.css';

const SeasonConfigModal = ({ teamId, baseTeamName, baseTeamLogo, onClose, onSave }) => {
  const [seasonTeamId, setSeasonTeamId] = useState('');
  const [seasonTeamName, setSeasonTeamName] = useState('');
  const [seasonTeamLogo, setSeasonTeamLogo] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const currentSeason = getCurrentSeason();

  useEffect(() => {
    // Initialize with base values
    setSeasonTeamId(teamId || '');
    setSeasonTeamName(baseTeamName || '');
    setSeasonTeamLogo(baseTeamLogo || '');
  }, [teamId, baseTeamName, baseTeamLogo]);

  const uploadLogoFile = async (file) => {
    if (!file) return;
    if (!teamId) {
      toast.error('Team ID not found');
      return;
    }

    const check = rateLimiter.checkLimit('UPLOAD_LOGO');
    if (!check.allowed) {
      const timeUntilReset = formatTimeUntilReset(rateLimiter.getTimeUntilReset('UPLOAD_LOGO'));
      toast.error(`Upload rate limit exceeded. Please try again in ${timeUntilReset}.`);
      return;
    }

    const { isValid, errors } = await validateFile(file, { type: 'logo', requireImageContent: true });
    if (!isValid) {
      errors.forEach((err) => toast.error(err));
      return;
    }

    if (!rateLimiter.record('UPLOAD_LOGO')) {
      const timeUntilReset = formatTimeUntilReset(rateLimiter.getTimeUntilReset('UPLOAD_LOGO'));
      toast.error(`Upload rate limit exceeded. Please try again in ${timeUntilReset}.`);
      return;
    }

    setUploadingLogo(true);
    try {
      const extRaw = file.name?.split('.').pop() || 'png';
      const ext = extRaw.replace(/[^a-z0-9]/gi, '').toLowerCase() || 'png';
      const fileName = `team-logo-${teamId}-${currentSeason}-${Date.now()}.${ext}`;
      const storagePath = `my-team/${teamId}/team-logos/${fileName}`;
      const storageRef = ref(storage, storagePath);

      await uploadBytes(storageRef, file);
      const logoUrl = await getDownloadURL(storageRef);
      setSeasonTeamLogo(logoUrl);
      toast.success('Team logo uploaded successfully');
    } catch (err) {
      console.error('Failed to upload team logo:', err);
      toast.error('Failed to upload team logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleFileInput = async (event) => {
    const file = event.target.files?.[0] || null;
    if (file) await uploadLogoFile(file);
    event.target.value = '';
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    setIsDragActive(false);
    if (uploadingLogo) return;
    const file = event.dataTransfer?.files?.[0] || null;
    if (file) await uploadLogoFile(file);
  };

  const handleSave = async () => {
    if (!seasonTeamId || !seasonTeamName) {
      toast.error('Team ID and Team Name are required');
      return;
    }

    setLoading(true);
    try {
      await saveSeason({
        teamId,
        year: currentSeason,
        displayName: seasonTeamName,
        externalTeamId: seasonTeamId,
        logo: seasonTeamLogo || baseTeamLogo || '',
        status: 'active',
      });

      toast.success(`${currentSeason} season configuration saved!`);
      onSave({
        teamId: seasonTeamId,
        teamName: seasonTeamName,
        teamLogo: seasonTeamLogo || baseTeamLogo,
      });
      onClose();
    } catch (error) {
      console.error('Failed to save season config:', error);
      toast.error('Failed to save season configuration');
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <div className="modal-overlay">
      <div className="save-modal" style={{ maxWidth: '500px' }}>
        <h2 style={{ marginBottom: '20px', color: '#fff' }}>
          Configure {currentSeason} Season
        </h2>
        
        <p style={{ color: '#94a3b8', marginBottom: '20px', fontSize: '14px' }}>
          A new season has started! Please configure your team information for {currentSeason}.
          This will be used for saving strategies and displaying your team information.
        </p>

        <div className="form-field" style={{ marginBottom: '16px' }}>
          <label style={{ color: '#ccc', marginBottom: '6px' }}>Team ID *</label>
          <input
            type="text"
            value={seasonTeamId}
            onChange={(e) => setSeasonTeamId(e.target.value)}
            placeholder="Enter team ID for this season"
            style={{
              padding: '10px 12px',
              borderRadius: '8px',
              backgroundColor: '#2a2a2a',
              color: 'white',
              border: '1px solid #444',
              fontSize: '14px',
              outline: 'none',
            }}
          />
          <small style={{ color: '#94a3b8', fontSize: '12px', marginTop: '4px' }}>
            This will be used when saving strategies. Can be different from your base team ID.
          </small>
        </div>

        <div className="form-field" style={{ marginBottom: '16px' }}>
          <label style={{ color: '#ccc', marginBottom: '6px' }}>Team Name *</label>
          <input
            type="text"
            value={seasonTeamName}
            onChange={(e) => setSeasonTeamName(e.target.value)}
            placeholder="Enter team name for this season"
            style={{
              padding: '10px 12px',
              borderRadius: '8px',
              backgroundColor: '#2a2a2a',
              color: 'white',
              border: '1px solid #444',
              fontSize: '14px',
              outline: 'none',
            }}
          />
        </div>

        <div className="form-field" style={{ marginBottom: '20px' }}>
          <label style={{ color: '#ccc', marginBottom: '6px' }}>Team Logo</label>
          <div
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            onDragOver={(event) => {
              event.preventDefault();
              if (!uploadingLogo) setIsDragActive(true);
            }}
            onDragLeave={(event) => {
              event.preventDefault();
              setIsDragActive(false);
            }}
            onDrop={handleDrop}
            style={{
              border: `1px dashed ${isDragActive ? '#60a5fa' : '#444'}`,
              backgroundColor: isDragActive ? 'rgba(59, 130, 246, 0.1)' : '#1f1f1f',
              borderRadius: '10px',
              padding: '16px',
              textAlign: 'center',
              color: '#cbd5f5',
              cursor: uploadingLogo ? 'not-allowed' : 'pointer',
              marginBottom: '10px',
              transition: 'all 0.2s ease',
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileInput}
              disabled={uploadingLogo}
              style={{ display: 'none' }}
            />
            <div style={{ fontSize: '14px', fontWeight: 600 }}>
              {uploadingLogo ? 'Uploading logo...' : 'Drag & drop logo here or click to upload'}
            </div>
            <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
              PNG, JPG, GIF, or WEBP (max 5MB)
            </div>
          </div>

          {seasonTeamLogo && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <img
                src={seasonTeamLogo}
                alt="Season logo preview"
                style={{ width: 44, height: 44, borderRadius: '8px', objectFit: 'cover', border: '1px solid #333' }}
              />
              <span style={{ color: '#94a3b8', fontSize: '12px' }}>Current logo preview</span>
            </div>
          )}

          <label style={{ color: '#ccc', marginBottom: '6px' }}>Team Logo URL (optional)</label>
          <input
            type="text"
            value={seasonTeamLogo}
            onChange={(e) => setSeasonTeamLogo(e.target.value)}
            placeholder="Enter team logo URL (optional)"
            style={{
              padding: '10px 12px',
              borderRadius: '8px',
              backgroundColor: '#2a2a2a',
              color: 'white',
              border: '1px solid #444',
              fontSize: '14px',
              outline: 'none',
            }}
          />
          <small style={{ color: '#94a3b8', fontSize: '12px', marginTop: '4px' }}>
            Upload a logo or paste a URL. Leave empty to use base team logo.
          </small>
        </div>

        <div className="modal-actions">
          <button
            onClick={handleSave}
            className="confirm-button"
            disabled={loading || uploadingLogo || !seasonTeamId || !seasonTeamName}
            style={{ opacity: (loading || uploadingLogo || !seasonTeamId || !seasonTeamName) ? 0.5 : 1 }}
          >
            {loading ? 'Saving...' : '✅ Save Configuration'}
          </button>
          <button onClick={onClose} className="cancel-button">
            ❌ Cancel
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return content;

  return createPortal(content, document.body);
};

export default SeasonConfigModal;

